// Apple iOS HIG Color System + SFM brand colors
// ─────────────────────────────────────────────────────────────────────────────
//
// Brand decisions (locked):
//   * Text labels stay "SFM" / "Shop Floor Mgmt".
//   * Brand chrome (nav + header surfaces): #1D2330 dark slate.
//   * Brand accent (primary buttons, focus, selection): #F88F1D amber.
//   * Status colors (success/warning/error/info) UNCHANGED — they carry
//     V-invariant + AISC GR §5.7 semantics across MustCloseHeaderCard, etc.
//
// Cross-platform parity:
//   * iOS SFMBrand.chrome  = #1D2330 → web palette.brand.chrome
//   * iOS SFMBrand.tint    = #F88F1D → web palette.brand.accent + primary.main
//   * iOS SFMBrand.onChrome = #FFFFFF → web palette.brand.onChrome
//
// Status colors (MustCloseHeaderCard hex tokens) remain authoritative for
// V-01..V-03 surface visibility — those must NOT shift to the brand accent.

const GREY = {
  0: '#FFFFFF',
  50: '#F9F9F9',
  100: '#F2F2F7',
  200: '#E5E5EA',
  300: '#D1D1D6',
  400: '#C7C7CC',
  500: '#A3A3A8',
  600: '#8E8E93',
  700: '#636366',
  800: '#3C3C43',
  900: '#1C1C1E',
};

// SFM amber — primary accent (formerly iOS System Blue #007AFF)
const PRIMARY = {
  lighter: '#FCD8A6',
  light: '#FAB360',
  main: '#F88F1D',
  dark: '#C26F0E',
  darker: '#8A4F09',
  contrastText: '#fff',
};

// SFM brand chrome — dark-slate surfaces for nav + header.
const BRAND = {
  chrome: '#1D2330',
  chromeElevated: '#252A37',
  onChrome: '#FFFFFF',
  onChromeSubtle: 'rgba(255,255,255,0.65)',
  onChromeDivider: 'rgba(255,255,255,0.10)',
  accent: '#F88F1D',
};

// A3 Red (brand secondary)
const SECONDARY = {
  lighter: '#EF9A9A',
  light: '#EF5350',
  main: '#D32F2F',
  dark: '#B71C1C',
  darker: '#7F0000',
  contrastText: '#fff',
};

// iOS System Green
const SUCCESS = {
  lighter: '#81C784',
  light: '#66BB6A',
  main: '#34C759',
  dark: '#2E8B57',
  darker: '#1B5E20',
  contrastText: '#fff',
};

// iOS System Orange
const WARNING = {
  lighter: '#FFB74D',
  light: '#FFA726',
  main: '#FF9F0A',
  dark: '#F57C00',
  darker: '#E65100',
  contrastText: '#fff',
};

// iOS System Red
const ERROR = {
  lighter: '#EF9A9A',
  light: '#E57373',
  main: '#FF3B30',
  dark: '#D32F2F',
  darker: '#B71C1C',
  contrastText: '#fff',
};

// iOS System Blue retained for INFO semantics (links, info banners, "i" callouts)
// — not for primary actions. Keeps MustCloseHeaderCard `info` variant working.
const INFO = {
  lighter: '#64B5F6',
  light: '#42A5F5',
  main: '#007AFF',
  dark: '#0066CC',
  darker: '#004DB8',
  contrastText: '#fff',
};

const palette = {
  mode: 'light',
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  brand: BRAND,
  divider: 'rgba(60,60,67,0.12)',
  text: {
    primary: '#1C1C1E',
    secondary: '#636366',
    disabled: '#A3A3A8',
  },
  background: {
    paper: '#FFFFFF',
    default: '#F2F2F7',
    neutral: '#E5E5EA',
  },
  action: {
    active: '#F88F1D',
    hover: 'rgba(248,143,29,0.08)',
    selected: 'rgba(248,143,29,0.16)',
    disabled: 'rgba(120,120,128,0.5)',
    disabledBackground: 'rgba(120,120,128,0.12)',
    focus: 'rgba(248,143,29,0.24)',
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};


// ── Dark mode ────────────────────────────────────────────────────────────────
// Reuses every brand/semantic scale (primary accent #F88F1D, brand chrome, info/
// success/warning/error, grey). Only surfaces, text, divider, and action tints
// flip. The nav/header already use brand.chrome (dark) so they are consistent in
// both modes. V-01..V-03 status colors are unchanged.
export const darkPalette = {
  ...palette,
  mode: 'dark',
  divider: 'rgba(255,255,255,0.12)',
  text: {
    primary: '#F2F3F5',
    secondary: '#A3ABB8',
    disabled: '#6B7280',
  },
  background: {
    paper: '#1C2027',
    default: '#14171D',
    neutral: '#242A30',
  },
  action: {
    ...palette.action,
    hover: 'rgba(248,143,29,0.12)',
    selected: 'rgba(248,143,29,0.22)',
    disabledBackground: 'rgba(255,255,255,0.10)',
  },
};

export default palette;
